<script>
import { mapGetters } from 'vuex';
import { subDays } from 'date-fns';
import { formatCustomSlaDuration } from 'dashboard/helper/customSlaTimeHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { h } from 'vue';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import V4Button from 'dashboard/components-next/button/Button.vue';
import DataTable from 'dashboard/components/table/Table.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import ReportHeader from './components/ReportHeader.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import DropdownButton from 'dashboard/components/ui/Dropdown/DropdownButton.vue';
import DropdownList from 'dashboard/components/ui/Dropdown/DropdownList.vue';
import CustomSlaReportLink from './components/CustomSla/CustomSlaReportLink.vue';
import CustomSlaBreakdownChart from './components/CustomSla/CustomSlaBreakdownChart.vue';
import CustomSlaBreachTypeChart from './components/CustomSla/CustomSlaBreachTypeChart.vue';
import CustomSlaMetricCards from './components/CustomSla/CustomSlaMetricCards.vue';
import CustomSlaOutcomePieChart from './components/CustomSla/CustomSlaOutcomePieChart.vue';
import CustomSlaConversationsTable from './components/CustomSla/CustomSlaConversationsTable.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import { useAlert } from 'dashboard/composables';

const GROUP_BY = {
  AGENT: 'agent',
  TEAM: 'team',
  UNASSIGNED: 'unassigned',
  GENERAL: 'general',
};

export default {
  components: {
    V4Button,
    DataTable,
    EmptyState,
    ReportHeader,
    WootDatePicker,
    DropdownButton,
    DropdownList,
    CustomSlaBreakdownChart,
    CustomSlaBreachTypeChart,
    CustomSlaMetricCards,
    CustomSlaOutcomePieChart,
    CustomSlaConversationsTable,
  },
  data() {
    return {
      groupBy: GROUP_BY.AGENT,
      customDateRange: [subDays(new Date(), 6), new Date()],
      selectedDateRangeType: 'last7days',
      showGroupByMenu: false,
      conversationsPageIndex: 0,
    };
  },
  computed: {
    ...mapGetters({
      metrics: 'customSlaReports/getMetrics',
      uiFlags: 'customSlaReports/getUIFlags',
    }),
    GROUP_BY: () => GROUP_BY,
    from() {
      return getUnixStartOfDay(this.customDateRange[0]);
    },
    to() {
      return getUnixEndOfDay(this.customDateRange[1]);
    },
    groupByOptions() {
      const options = [
        {
          id: GROUP_BY.AGENT,
          name: this.$t('CUSTOM_SLA_REPORTS.GROUP_BY.AGENT'),
        },
      ];
      if (this.metrics.teamsAvailable) {
        options.push({
          id: GROUP_BY.TEAM,
          name: this.$t('CUSTOM_SLA_REPORTS.GROUP_BY.TEAM'),
        });
      }
      options.push(
        {
          id: GROUP_BY.UNASSIGNED,
          name: this.$t('CUSTOM_SLA_REPORTS.GROUP_BY.UNASSIGNED'),
        },
        {
          id: GROUP_BY.GENERAL,
          name: this.$t('CUSTOM_SLA_REPORTS.GROUP_BY.GENERAL'),
        }
      );
      return options;
    },
    selectedGroupByName() {
      const selected = this.groupByOptions.find(
        option => option.id === this.groupBy
      );
      return selected?.name;
    },
    isTableView() {
      return this.groupBy === GROUP_BY.AGENT || this.groupBy === GROUP_BY.TEAM;
    },
    isUnassignedView() {
      return this.groupBy === GROUP_BY.UNASSIGNED;
    },
    isGeneralView() {
      return this.groupBy === GROUP_BY.GENERAL;
    },
    activeRows() {
      return this.groupBy === GROUP_BY.AGENT
        ? this.metrics.byAgent
        : this.metrics.byTeam;
    },
    tableData() {
      return this.activeRows.map(row => ({
        id: row.id,
        type: this.groupBy,
        name: row.name,
        conversationsCount: row.conversations_count || 0,
        avgFirstResponseTime: row.avg_first_response_time
          ? formatCustomSlaDuration(row.avg_first_response_time, this.$t)
          : '--',
        avgResolutionTime: row.avg_resolution_time
          ? formatCustomSlaDuration(row.avg_resolution_time, this.$t)
          : '--',
      }));
    },
    unassignedConversationsCount() {
      return this.metrics.unassigned.conversations_count || 0;
    },
    unassignedAvgFirstResponseTime() {
      return this.metrics.unassigned.avg_first_response_time
        ? formatCustomSlaDuration(
            this.metrics.unassigned.avg_first_response_time,
            this.$t
          )
        : '--';
    },
    unassignedAvgResolutionTime() {
      return this.metrics.unassigned.avg_resolution_time
        ? formatCustomSlaDuration(
            this.metrics.unassigned.avg_resolution_time,
            this.$t
          )
        : '--';
    },
    generalConversationsCount() {
      return this.metrics.general.conversations_count || 0;
    },
    generalAvgFirstResponseTime() {
      return this.metrics.general.avg_first_response_time
        ? formatCustomSlaDuration(
            this.metrics.general.avg_first_response_time,
            this.$t
          )
        : '--';
    },
    generalAvgResolutionTime() {
      return this.metrics.general.avg_resolution_time
        ? formatCustomSlaDuration(
            this.metrics.general.avg_resolution_time,
            this.$t
          )
        : '--';
    },
    generalHitCount() {
      return this.metrics.general.hit_count || 0;
    },
    generalMissedCount() {
      return this.metrics.general.missed_count || 0;
    },
    generalNextResponseMetCount() {
      return this.metrics.general.next_response_met_count || 0;
    },
    generalNextResponseMissedCount() {
      return this.metrics.general.next_response_missed_count || 0;
    },
    table() {
      const vm = this;
      const columnHelper = createColumnHelper();
      const defaultSpanRender = cellProps =>
        h('span', {}, cellProps.getValue());
      const columns = [
        columnHelper.accessor('name', {
          header:
            vm.groupBy === GROUP_BY.AGENT
              ? vm.$t('CUSTOM_SLA_REPORTS.TABLE_HEADER.AGENT')
              : vm.$t('CUSTOM_SLA_REPORTS.TABLE_HEADER.TEAM'),
          cell: cellProps => h(CustomSlaReportLink, cellProps),
        }),
        columnHelper.accessor('conversationsCount', {
          header: vm.$t('CUSTOM_SLA_REPORTS.TABLE_HEADER.CONVERSATIONS'),
          cell: defaultSpanRender,
        }),
        columnHelper.accessor('avgFirstResponseTime', {
          header: vm.$t(
            'CUSTOM_SLA_REPORTS.TABLE_HEADER.AVG_FIRST_RESPONSE_TIME'
          ),
          cell: defaultSpanRender,
        }),
        columnHelper.accessor('avgResolutionTime', {
          header: vm.$t('CUSTOM_SLA_REPORTS.TABLE_HEADER.AVG_RESOLUTION_TIME'),
          cell: defaultSpanRender,
        }),
      ];
      return useVueTable({
        get data() {
          return vm.tableData;
        },
        columns,
        enableSorting: false,
        getCoreRowModel: getCoreRowModel(),
      });
    },
  },
  watch: {
    'metrics.teamsAvailable': function handleTeamsAvailableChange(available) {
      if (!available && this.groupBy === GROUP_BY.TEAM) {
        this.groupBy = GROUP_BY.AGENT;
      }
    },
  },
  mounted() {
    this.fetchMetrics();
  },
  methods: {
    fetchMetrics() {
      this.$store.dispatch('customSlaReports/getMetrics', {
        from: this.from,
        to: this.to,
      });
    },
    fetchConversations() {
      this.$store.dispatch('customSlaReports/getConversations', {
        from: this.from,
        to: this.to,
        page: this.conversationsPageIndex + 1,
      });
    },
    onDateRangeChange([startDate, endDate, rangeType]) {
      this.customDateRange = [startDate, endDate];
      this.selectedDateRangeType = rangeType;
      this.fetchMetrics();
      if (this.isGeneralView) {
        this.conversationsPageIndex = 0;
        this.fetchConversations();
      }
    },
    onConversationsPageChange(pageIndex) {
      this.conversationsPageIndex = pageIndex;
      this.fetchConversations();
    },
    toggleGroupByMenu() {
      this.showGroupByMenu = !this.showGroupByMenu;
    },
    closeGroupByMenu() {
      this.showGroupByMenu = false;
    },
    selectGroupBy(option) {
      this.groupBy = option.id;
      this.closeGroupByMenu();
      if (this.isGeneralView) {
        this.conversationsPageIndex = 0;
        this.fetchConversations();
      }
    },
    downloadReport() {
      try {
        this.$store.dispatch('customSlaReports/download', {
          fileName: generateFileName({ type: 'custom-sla', to: this.to }),
          from: this.from,
          to: this.to,
        });
      } catch (error) {
        useAlert(this.$t('CUSTOM_SLA_REPORTS.DOWNLOAD_FAILED'));
      }
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('CUSTOM_SLA_REPORTS.HEADER')">
    <V4Button
      :label="$t('CUSTOM_SLA_REPORTS.DOWNLOAD_ACTION')"
      icon="i-ph-download-simple"
      size="sm"
      @click="downloadReport"
    />
  </ReportHeader>
  <div class="flex flex-col flex-1 gap-6">
    <div
      class="flex flex-col flex-wrap w-full gap-3 md:flex-row md:items-center md:justify-between"
    >
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRangeType"
        @date-range-changed="onDateRangeChange"
      />
      <DropdownButton
        trailing-icon
        icon="i-lucide-chevron-down"
        :button-text="selectedGroupByName"
        @click="toggleGroupByMenu"
      >
        <template v-if="showGroupByMenu" #dropdown>
          <DropdownList
            v-on-clickaway="closeGroupByMenu"
            :list-items="groupByOptions"
            :active-filter-id="groupBy"
            class="flex flex-col w-56 left-0 md:left-auto md:right-0 top-10"
            @select="selectGroupBy"
          />
        </template>
      </DropdownButton>
    </div>

    <woot-loading-state
      v-if="uiFlags.isFetchingMetrics"
      :message="$t('CUSTOM_SLA_REPORTS.LOADING')"
    />
    <template v-else-if="isTableView">
      <div
        class="relative flex-1 overflow-auto px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <DataTable :table="table" />
        <EmptyState
          v-if="!tableData.length"
          :title="$t('CUSTOM_SLA_REPORTS.NO_DATA')"
        />
      </div>
      <CustomSlaBreakdownChart
        :title="$t('CUSTOM_SLA_REPORTS.CHARTS.MOST_BROKEN_POLICY')"
        :rows="metrics.byPolicy"
        value-key="total"
        :fallback-name="$t('CUSTOM_SLA_REPORTS.UNKNOWN_POLICY')"
        color="rgb(124, 58, 237)"
        :empty-message="$t('CUSTOM_SLA_REPORTS.NO_DATA')"
      />
    </template>
    <template v-else-if="isUnassignedView">
      <CustomSlaMetricCards
        :conversations-count="unassignedConversationsCount"
        :avg-first-response-time="unassignedAvgFirstResponseTime"
        :avg-resolution-time="unassignedAvgResolutionTime"
      />
      <CustomSlaBreachTypeChart
        :row="metrics.unassigned"
        :title="$t('CUSTOM_SLA_REPORTS.CHARTS.SLA_HISTORY')"
        :empty-message="$t('CUSTOM_SLA_REPORTS.NO_DATA')"
      />
    </template>
    <template v-else-if="isGeneralView">
      <CustomSlaMetricCards
        :conversations-count="generalConversationsCount"
        :avg-first-response-time="generalAvgFirstResponseTime"
        :avg-resolution-time="generalAvgResolutionTime"
      />
      <CustomSlaBreachTypeChart
        :row="metrics.general"
        :empty-message="$t('CUSTOM_SLA_REPORTS.NO_DATA')"
      />
      <div class="flex flex-col w-full gap-6 sm:flex-row">
        <CustomSlaOutcomePieChart
          :hit-count="generalHitCount"
          :missed-count="generalMissedCount"
          :empty-message="$t('CUSTOM_SLA_REPORTS.NO_DATA')"
          class="w-full sm:max-w-sm self-start"
        />
        <CustomSlaOutcomePieChart
          :hit-count="generalNextResponseMetCount"
          :missed-count="generalNextResponseMissedCount"
          :title="$t('CUSTOM_SLA_REPORTS.CHARTS.NEXT_RESPONSE_OUTCOME')"
          met-label-key="CUSTOM_SLA_REPORTS.CHARTS.NEXT_RESPONSE_MET"
          missed-label-key="CUSTOM_SLA_REPORTS.CHARTS.NEXT_RESPONSE_MISSED"
          :empty-message="$t('CUSTOM_SLA_REPORTS.NO_DATA')"
          class="w-full sm:max-w-sm self-start"
        />
      </div>
      <CustomSlaConversationsTable
        :page-index="conversationsPageIndex"
        @page-change="onConversationsPageChange"
      />
    </template>
  </div>
</template>
